// Display helpers for the admin-only AI coaching beta (owner decision D18).

export const RATING_LABELS = { useful: 'Useful', generic: 'Generic', wrong: 'Wrong' }

export const FLAG_LABELS = {
  no_player_context_link: 'not linked to a goal, history entry, the charter or a key play',
  generic_wording: 'could apply to any player (specificity screen)',
  repeated_cue: 'repeats another statement'
}

export const flagText = flags => flags?.length ? `Possibly generic: ${flags.map(flag => FLAG_LABELS[flag] || flag).join('; ')}.` : ''

// "Highlights API 403: {"detail":"..."}" -> the API's own sentence.
export const apiErrorText = (error, fallback = 'Something went wrong. Retry in a moment.') => {
  const match = /^Highlights API (\d+): (.*)$/s.exec(String(error?.message || ''))
  if (!match) return error?.message || fallback
  try {
    const detail = JSON.parse(match[2])?.detail
    if (typeof detail === 'string' && detail) return detail
  } catch (_) {}
  return fallback
}

export const timestamp = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`

// A bounded range (a highlight, or any start/end other than the whole video) is clip coaching.
export const isClipRange = (start, end) => (end !== null && end !== undefined && end !== '') || Number(start || 0) > 0

const near = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && Math.abs(Number(a) - Number(b)) < 0.01

// The newest clip coaching run made for this highlight's exact range (jobs arrive newest first).
export const clipJobFor = (jobs = [], highlight) => highlight
  ? jobs.find(job => job.clip && job.status !== 'cancelled' && near(job.start_seconds, highlight.start) && near(job.end_seconds, highlight.end)) || null
  : null

// The highlight a clip run was made for, by its range.
export const highlightForRange = (highlights = [], start, end) =>
  highlights.find(item => near(item.start, start) && near(item.end, end)) || null

export const rangeText = (start, end) => `${timestamp(Number(start || 0))}–${end === null || end === undefined || end === '' ? 'end' : timestamp(Number(end))}`

export const clipRunLabel = (name, start, end) => `Clip coaching: ${name || 'clip'} (${rangeText(start, end)})`

// D20: the clip itself shows queued / running / ready / failed.
export const clipCoachingState = job => {
  if (!job) return { state: 'none', label: 'No coaching yet', color: 'default' }
  if (job.status === 'queued') return { state: 'queued', label: 'Coaching queued', color: 'info' }
  if (['launching', 'running'].includes(job.status)) return { state: 'running', label: `Coaching running${job.progress ? ` · ${job.progress}%` : ''}`, color: 'info' }
  if (job.status === 'complete') return { state: 'ready', label: 'Coaching ready', color: 'success' }
  return { state: 'failed', label: 'Coaching failed', color: 'error' }
}

// Every rateable statement, with the same IDs the API uses for this coaching revision.
export const coachingStatements = coaching => {
  if (!coaching) return []
  return [
    { id: 'overview', label: 'Overview', text: coaching.overview },
    ...(coaching.key_plays || []).map((play, index) => ({ id: `key_play:${index}`, label: play.title, text: play.cue, play })),
    ...(coaching.next_priorities || []).map((text, index) => ({ id: `priority:${index}`, label: 'Next practice', text }))
  ]
}

// The latest current rating per statement for this coaching revision.
export const currentRatings = (feedback = [], revision) => {
  const result = {}
  for (const row of feedback) {
    if (row.current && (!revision || row.coaching_revision === revision)) result[row.statement_id] = row
  }
  return result
}

const integer = value => Number(value || 0).toLocaleString('en-US')

export const plural = (count, word) => `${count} ${word}${String(count) === '1' ? '' : 's'}`

export const money = (value, currency = 'USD') => {
  if (value === null || value === undefined) return 'unknown'
  const digits = value < 0.01 ? 4 : 2
  return `${currency === 'USD' ? '$' : `${currency} `}${Number(value).toFixed(digits)}`
}

// The short cost figure for a run: dollars, "not set up" (no prices saved yet) or "unknown".
export const estimateLabel = report => {
  if (!report) return '-'
  const estimate = report.estimate || {}
  if (estimate.status === 'estimated') return money(estimate.total, estimate.currency)
  const reasons = estimate.unknown_reasons || []
  return !reasons.length || reasons.every(reason => reason === 'no price table is set up yet') ? 'not set up' : 'unknown'
}

// One row per usage category and a plain-language estimate line.
export const usageSummary = report => {
  if (!report) return null
  const estimate = report.estimate || {}
  const missing = estimateLabel({ estimate: { ...estimate, status: 'unknown' } })
  const rows = (report.categories || []).map(item => ({
    name: item.name,
    label: item.label,
    calls: item.calls === null || item.calls === undefined ? 'not recorded' : integer(item.calls),
    failed: item.failed_calls ? integer(item.failed_calls) : '0',
    input: integer(item.input_tokens),
    output: integer(item.output_tokens),
    cost: item.estimated_cost === null || item.estimated_cost === undefined ? missing : money(item.estimated_cost, estimate.currency)
  }))
  const known = estimate.status === 'estimated'
  const provenance = [estimate.source && `prices from ${estimate.source}`, estimate.as_of && `as of ${estimate.as_of}`].filter(Boolean).join(', ')
  const reasons = estimate.unknown_reasons || []
  const notSetUp = !reasons.length || reasons.every(reason => reason === 'no price table is set up yet')
  const minutes = report.worker_seconds === null || report.worker_seconds === undefined ? null : report.worker_seconds / 60
  return {
    rows,
    totals: { calls: integer(report.totals?.calls), input: integer(report.totals?.input_tokens), output: integer(report.totals?.output_tokens) },
    headline: known
      ? `Estimated model (token) cost: ${money(estimate.total, estimate.currency)}${provenance ? ` (${provenance})` : ''}`
      : notSetUp
        ? 'Cost estimate not set up yet. Token counts are below. (Admin: add the model price table under Admin > AI coaching beta.)'
        : `Model (token) cost unknown: ${reasons.join('; ')}.`,
    estimateText: estimateLabel(report),
    workerTime: minutes === null ? null : `Worker time: ${minutes < 1 ? `${Math.round(report.worker_seconds)} s` : `${minutes.toFixed(1)} min`} (compute is not included in the estimate)`,
    label: estimate.label || '',
    model: report.model || 'unknown model'
  }
}

const context = value => value || {}

// "What this report used": the frozen snapshot, grouped for reading, plus what was left out.
export const contextSummary = (snapshot, used, digest) => {
  if (!snapshot) {
    return {
      status: 'none',
      message: 'No player context was recorded. AI coaching is an admin-only beta, and this run did not write coaching from a player profile.'
    }
  }
  const player = context(snapshot.player)
  const game = context(snapshot.game)
  const excluded = context(snapshot.excluded)
  const status = used?.used_by_coaching ? 'used' : 'recorded'
  const left = [
    excluded.withdrawn && `${excluded.withdrawn} withdrawn`,
    excluded.after_cutoff && `${excluded.after_cutoff} after the cutoff`,
    excluded.other_author && `${excluded.other_author} written by someone else`,
    excluded.over_limit && `${excluded.over_limit} older entries beyond the limit`
  ].filter(Boolean)
  return {
    status,
    message: status === 'used'
      ? 'Coaching was written from this snapshot. Nothing added or changed after the cutoff was used.'
      : 'This snapshot was recorded for the run, but no coaching was written from it.',
    cutoff: snapshot.cutoff,
    profile: [player.name, player.position, player.team, player.jersey && `#${player.jersey}`].filter(Boolean).join(' · '),
    profileRevision: player.profile_revision,
    game: [game.team, game.jersey && `#${game.jersey}`, game.position && game.position !== 'unknown' && game.position, game.uniform].filter(Boolean).join(' · '),
    charter: snapshot.charter?.text || '',
    goals: (snapshot.goals || []).map(entry => ({ ref: entry.ref, text: entry.content, date: entry.observed_at?.slice(0, 10) })),
    history: (snapshot.history || []).map(entry => ({ ref: entry.ref, kind: entry.kind.replace('_', ' '), text: entry.content, date: entry.observed_at?.slice(0, 10) })),
    leftOut: left.length ? `Left out: ${left.join(', ')}.` : 'Nothing eligible was left out.',
    knowledge: (snapshot.knowledge_cards || []).length ? `${snapshot.knowledge_cards.length} knowledge cards` : 'No shared coaching cards (none are eligible yet).',
    digest: (used?.digest || digest || '').slice(0, 12) || null
  }
}

// Names a cited player-context reference with its text, for "Linked to" lines.
export const contextLabel = (snapshot, ref) => {
  if (ref === 'CHARTER') return 'Development charter'
  const entry = [...(snapshot?.goals || []), ...(snapshot?.history || [])].find(item => item.ref === ref)
  return entry ? `${ref}: ${entry.content}` : ref
}

export const saveJsonFile = (data, filename) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
